import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { brl, fdatetime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Search, Pencil, Trash2, Loader2, ArrowUpDown, History } from "lucide-react";

function StockDialog({ open, onOpenChange, product }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState("entrada");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setMode("entrada");
      setQty("");
      setReason("");
    }
  }, [open]);

  const save = async () => {
    const q = parseInt(qty);
    if (!q || q <= 0) {
      toast.error("Informe a quantidade");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/products/${product.id}/stock`, { delta: mode === "entrada" ? q : -q, reason });
      toast.success(`Estoque atualizado: ${r.data.stock} un`);
      qc.invalidateQueries();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="stock-dialog">
        <DialogHeader>
          <DialogTitle>Movimentar estoque — {product?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Estoque atual: <span className="num font-semibold text-foreground">{product?.stock} un</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {["entrada", "saida"].map((m) => (
              <button
                key={m}
                data-testid={`stock-mode-${m}`}
                onClick={() => setMode(m)}
                className={`rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                  mode === m
                    ? m === "entrada"
                      ? "border-emerald-500 bg-emerald-500/10 text-emerald-500"
                      : "border-red-500 bg-red-500/10 text-red-500"
                    : "border-border text-muted-foreground hover:bg-accent"
                }`}
              >
                {m === "entrada" ? "Entrada (+)" : "Saída (−)"}
              </button>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label>Quantidade</Label>
            <Input type="number" min="1" data-testid="stock-qty" value={qty} onChange={(e) => setQty(e.target.value)} className="num" />
          </div>
          <div className="space-y-1.5">
            <Label>Motivo</Label>
            <Input data-testid="stock-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: compra de fornecedor, perda, ajuste" />
          </div>
          <Button data-testid="stock-save" onClick={save} disabled={saving} className="w-full h-11">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirmar movimentação
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ open, onOpenChange, product }) {
  const { data, isLoading } = useQuery({
    queryKey: ["stock-movs", product?.id],
    queryFn: async () => (await api.get(`/products/${product.id}/stock-movements`)).data,
    enabled: open && !!product,
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="stock-history-dialog">
        <DialogHeader>
          <DialogTitle>Histórico de estoque — {product?.name}</DialogTitle>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto divide-y divide-border">
          {isLoading && <Skeleton className="h-10" />}
          {(data || []).map((m) => (
            <div key={m.id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`stock-mov-${m.id}`}>
              <div>
                <div className="font-medium">{m.reason || "Movimentação"}</div>
                <div className="text-xs text-muted-foreground">{fdatetime(m.created_at)} · {m.user_name}</div>
              </div>
              <div className="text-right">
                <div className={`num font-semibold ${m.delta >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                  {m.delta >= 0 ? "+" : ""}{m.delta}
                </div>
                <div className="num text-xs text-muted-foreground">{m.before} → {m.after}</div>
              </div>
            </div>
          ))}
          {data && data.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">Nenhuma movimentação manual registrada.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY = { name: "", category: "", price: "", code: "", stock: 0, min_stock: 0, supplier: "", track_stock: true, active: true };

export default function Produtos() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = user.role === "admin" || !!user.can_edit_stock;
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [stockProduct, setStockProduct] = useState(null);
  const [historyProduct, setHistoryProduct] = useState(null);

  const { data: products, isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
  });

  useEffect(() => {
    if (open) setForm(editing ? { ...editing } : EMPTY);
  }, [open, editing]);

  const filtered = (products || []).filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  const save = async () => {
    if (!form.name.trim() || !form.price) {
      toast.error("Nome e preço são obrigatórios");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(String(form.price).replace(",", ".")) || 0,
      stock: parseInt(form.stock) || 0,
      min_stock: parseInt(form.min_stock) || 0,
      category: form.category || "Geral",
    };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Produto atualizado" : "Produto cadastrado");
      qc.invalidateQueries();
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Produto removido");
      qc.invalidateQueries();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-6" data-testid="produtos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Produtos</h1>
          <p className="text-sm text-muted-foreground">Catálogo, preços e estoque</p>
        </div>
        {canEdit && (
          <Button data-testid="new-product-btn" onClick={() => { setEditing(null); setOpen(true); }}>
            <Plus className="mr-1 h-4 w-4" /> Novo produto
          </Button>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="products-search" placeholder="Buscar produto..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          {isLoading ? (
            <div className="p-6 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : (
            <Table data-testid="products-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Preço</TableHead>
                  <TableHead className="text-right">Estoque</TableHead>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead>Status</TableHead>
                  {canEdit && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell><Badge variant="outline">{p.category}</Badge></TableCell>
                    <TableCell className="num text-right font-semibold">{brl(p.price)}</TableCell>
                    <TableCell className="text-right">
                      {p.track_stock ? (
                        <Badge
                          variant="outline"
                          className={p.stock <= p.min_stock ? "border-amber-500/40 text-amber-500" : ""}
                          data-testid={`product-stock-${p.id}`}
                        >
                          {p.stock} un
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Serviço</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{p.supplier || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={p.active ? "border-emerald-500/40 text-emerald-500" : "border-muted-foreground/40 text-muted-foreground"}>
                        {p.active ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    {canEdit && (
                      <TableCell>
                        <div className="flex gap-1">
                          {p.track_stock && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Movimentar estoque"
                                data-testid={`stock-product-${p.id}`} onClick={() => setStockProduct(p)}>
                                <ArrowUpDown className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Histórico de estoque"
                                data-testid={`history-product-${p.id}`} onClick={() => setHistoryProduct(p)}>
                                <History className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-product-${p.id}`}
                            onClick={() => { setEditing(p); setOpen(true); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500"
                            data-testid={`delete-product-${p.id}`} onClick={() => remove(p)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                      Nenhum produto encontrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar produto" : "Novo produto"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Nome *</Label>
              <Input data-testid="product-name" value={form.name} onChange={set("name")} />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Input data-testid="product-category" value={form.category} onChange={set("category")} placeholder="Suplementos" />
            </div>
            <div className="space-y-1.5">
              <Label>Preço (R$) *</Label>
              <Input data-testid="product-price" inputMode="decimal" value={form.price} onChange={set("price")} className="num" />
            </div>
            <div className="space-y-1.5">
              <Label>Código</Label>
              <Input data-testid="product-code" value={form.code} onChange={set("code")} />
            </div>
            <div className="space-y-1.5">
              <Label>Fornecedor</Label>
              <Input data-testid="product-supplier" value={form.supplier} onChange={set("supplier")} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <Label>Controla estoque</Label>
              <Switch data-testid="product-track-stock" checked={form.track_stock} onCheckedChange={(v) => setForm((f) => ({ ...f, track_stock: v }))} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <Label>Ativo</Label>
              <Switch data-testid="product-active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
            </div>
            {form.track_stock && (
              <>
                <div className="space-y-1.5">
                  <Label>Estoque</Label>
                  <Input type="number" data-testid="product-stock" value={form.stock} onChange={set("stock")} className="num" />
                </div>
                <div className="space-y-1.5">
                  <Label>Estoque mínimo</Label>
                  <Input type="number" data-testid="product-min-stock" value={form.min_stock} onChange={set("min_stock")} className="num" />
                </div>
              </>
            )}
          </div>
          <Button data-testid="product-save" onClick={save} disabled={saving} className="mt-2 w-full h-11">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogContent>
      </Dialog>

      <StockDialog open={!!stockProduct} onOpenChange={() => setStockProduct(null)} product={stockProduct} />
      <HistoryDialog open={!!historyProduct} onOpenChange={() => setHistoryProduct(null)} product={historyProduct} />
    </div>
  );
}
